import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  IAccountPermission,
  IPermissionCategories,
} from "../features/Configurations/TeamManagement/interface";
import {
  IAccountOption,
  IIntegrationOption,
  ILoginPayload,
  ILoginResponse,
  IMerchant,
  ISignUpPayload,
  IUserPreferences,
  IViewAsPayload,
  SignupResponse,
} from "../interfaces";
import { httpService } from "../services/httpService";
import { API_CONFIG, DEFAULT_SELECTED_ACCOUNT } from "../shared/constants";
import { AuthContext } from "./AuthContextDefinition";
import { getPermittedRoutes, RouteConfig } from "../config/routes";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { decryptData } from "../utils/encryption";
import { useWebSocket } from "../hooks/useWebSocket";

interface AuthProviderProps {
  children: ReactNode;
}

const SELECTED_ACCOUNT_KEY = "selected_account";
const SELECTED_INTEGRATION_KEY = "selected_integration";

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<IMerchant | null>(null);
  const [sessionId, setSessionId] = useState(httpService.getSessionId() || "");
  const [permittedRoutes, setPermittedRoutes] = useState<RouteConfig[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<IAccountOption>(() => {
    const savedAccount = localStorage.getItem(SELECTED_ACCOUNT_KEY);
    return savedAccount ? JSON.parse(savedAccount) : DEFAULT_SELECTED_ACCOUNT;
  });
  const [selectedIntegration, setSelectedIntegration] = useState<IIntegrationOption | null>(() => {
    const savedIntegration = localStorage.getItem(SELECTED_INTEGRATION_KEY);
    return savedIntegration ? JSON.parse(savedIntegration) : null;
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loggingOut, setLoggingOut] = useState(false);
  // Data sync no longer uses tracking ids
  const [taskId, setTaskId] = useState<string | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const WEBSOCKET_URL = import.meta.env.VITE_WEB_SOCKET_URL;
  const clearAllData = useCallback(() => {
    // Clear HTTP service data (tokens, user data)
    httpService.clearAllData();
    queryClient.clear();

    // Clear localStorage
    localStorage.clear();

    // Clear sessionStorage
    sessionStorage.clear();

    // Clear all cookies
    document.cookie.split(";").forEach((cookie) => {
      const [name] = cookie.split("=");
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }, [queryClient]);

  // Define logout function early so it can be used in WebSocket hook
  const logout = useCallback(async () => {
    try {
      setLoggingOut(true);
      setSessionId("");
      setTaskId(null);
      await httpService.logout();
      setSelectedAccount(DEFAULT_SELECTED_ACCOUNT);
      clearAllData();
      setUser(null);
      navigate("/auth");
    } catch (error) {
      toast.error(
        (error as Error)?.message || "Logout failed. Please try again."
      );
    } finally {
      setLoggingOut(false);
    }
  }, [clearAllData, navigate]);

  // Use WebSocket hook for session heartbeat
  const {
    wsConnected,
    lastPing,
    clearPingInterval,
    resetSessionRestored,
    sendLogout,
  } = useWebSocket({
    sessionId,
    userId: user?.id?.toString(),
    wsUrl: WEBSOCKET_URL,
    onSessionInactive: logout,
  });



  const teams = useMemo(() => {
    return user?.team_members || [];
  }, [user]);

  // Save selected account to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(
        SELECTED_ACCOUNT_KEY,
        JSON.stringify(selectedAccount)
      );
    }
  }, [selectedAccount, user]);

  useEffect(() => {
    if (user && selectedIntegration) {
      localStorage.setItem(
        SELECTED_INTEGRATION_KEY,
        JSON.stringify(selectedIntegration)
      );
    }
  }, [selectedIntegration, user]);

  useEffect(() => {
    if (user) {
      if (
        (typeof user.user_permissions === "object" &&
          (user.user_permissions as IPermissionCategories)?.account?.length ===
          1) ||
        user.team_members?.length === 1
      ) {
        const userAcc: IAccountPermission | IMerchant =
          ((user.user_permissions as IPermissionCategories)?.account ||
            [])[0] || (user.team_members || [])[0];
        const acc = {
          label: userAcc.name,
          value:
            (userAcc as IAccountPermission).codename ||
            (userAcc as unknown as IMerchant).slug ||
            "",
          id: userAcc.id,
          profile_picture: userAcc.profile_picture || null,
          name: userAcc.name,
          email: userAcc.email,
        };
        setSelectedAccount(acc);
      }
    }
  }, [user]);

  useEffect(() => {
    // Check if user is already logged in by checking access token
    const accessToken = httpService.getAccessToken();

    if (accessToken && !user) {
      // Fetch user data from API instead of local storage
      httpService
        .getCurrentUser()
        .then((userData) => {
          setUser(userData);
          const permittedRoutes = getPermittedRoutes(userData);
          setPermittedRoutes(permittedRoutes);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Failed to fetch user data:", error);
          // If we can't fetch user data, clear everything and redirect to login
          clearAllData();
          setUser(null);
          setIsLoading(false);
          navigate("/auth");
        });
    } else {
      setIsLoading(false);
    }
  }, [clearAllData, navigate, sessionId, user]);

  const login = async (credentials: ILoginPayload) => {
    try {
      const response = await httpService.post<ILoginResponse>(
        API_CONFIG.path.login,
        { ...credentials } as unknown as Record<string, unknown>,
        {}, // query params
        { headers: { Authorization: undefined } } // don't send auth header for login
      );
      const { user: responseUser, access, refresh, session_id } = response;

      if (responseUser.is_deleted) {
        throw new Error("User is deleted");
      }

      if (responseUser.is_active === false) {
        throw new Error("User is inactive");
      }

      // Store tokens and user data securely
      httpService.clearTakeOverUser();
      httpService.setTokens({ access, refresh, sessionId: session_id });
      httpService.setUserData(responseUser);

      if (!localStorage.getItem("i18nextLng")) {
        localStorage.setItem("i18nextLng", "en");
      }
      // Set user in state and navigate
      await httpService.getCurrentUser().then((userData) => {
        if (session_id) {
          setSessionId(session_id);
        }
        setUser(userData);
        const permittedRoutes = getPermittedRoutes(userData);
        setPermittedRoutes(permittedRoutes);
      });
      // setTimeout(() => {
      //   window.location.href = (permittedRoutes[0] || {})?.path || "/dashboard";
      // }, 500);
    } catch (error) {
      throw new Error((error as Error).message || "");
    }
  };

  const takeOverLogin = async (payload: IViewAsPayload) => {
    httpService.clearAllData();
    setSelectedAccount(DEFAULT_SELECTED_ACCOUNT);
    try {
      const response = await httpService.post<ILoginResponse>(
        API_CONFIG.path.viewAs,
        { encryptedEmail: payload.encryptedEmail } as unknown as Record<
          string,
          unknown
        >,
        {}, // query params
        {
          headers: {
            Authorization: `Bearer ${decryptData(payload.accessToken)}`,
          },
        }
      );
      const { user: responseUser, access, refresh } = response;

      if (responseUser.is_deleted) {
        throw new Error("User is deleted");
      }

      if (responseUser.is_active === false) {
        throw new Error("User is inactive");
      }

      // Store tokens and user data securely
      httpService.setTakeOverUser(true);
      httpService.setTokens({ access, refresh });
      httpService.setUserData(responseUser);

      if (!localStorage.getItem("i18nextLng")) {
        localStorage.setItem("i18nextLng", "en");
      }
      // Set user in state and navigate
      await httpService.getCurrentUser().then((userData) => {
        setUser(userData);
        const permittedRoutes = getPermittedRoutes(userData);
        setPermittedRoutes(permittedRoutes);
      });
      // setTimeout(() => {
      //   window.location.href = (permittedRoutes[0] || {})?.path || "/dashboard";
      // }, 500);
    } catch (error) {
      throw new Error((error as Error).message || "");
    }
  };

  // Update logout to use WebSocket cleanup functions
  const enhancedLogout = useCallback(async () => {
    try {
      setLoggingOut(true);
      // Stop heartbeat and close WS before clearing auth
      clearPingInterval();
      resetSessionRestored(); // reset restoration flag on logout
      await logout(); // Call the base logout function
    } catch (error) {
      toast.error(
        (error as Error)?.message || "Logout failed. Please try again."
      );
    } finally {
      setLoggingOut(false);
    }
  }, [logout, clearPingInterval, resetSessionRestored]);

  const signup = async (userData: ISignUpPayload) => {
    try {
      const response = await httpService.post<SignupResponse>(
        API_CONFIG.path.register,
        { ...userData } as unknown as Record<string, unknown>,
        {}, // query params
        { headers: { Authorization: undefined } } // don't send auth header for signup
      );

      const { user: responseUser, access, refresh } = response;

      if (!responseUser.role) {
        throw new Error("User roles not found");
      }

      // Store tokens and user data securely
      httpService.setTokens({ access, refresh });
      httpService.setUserData(responseUser);

      if (!localStorage.getItem("i18nextLng")) {
        localStorage.setItem("i18nextLng", "en");
      }
      // Set user in state and navigate
      httpService.getCurrentUser().then((userData) => {
        setUser(userData);
        const permittedRoutes = getPermittedRoutes(userData);
        setPermittedRoutes(permittedRoutes);
      });
      // setTimeout(() => {
      //   window.location.href = (permittedRoutes[0] || {})?.path || "/dashboard";
      // }, 500);
    } catch (error) {
      console.error("Signup error:", error);
      throw new Error((error as Error).message || "");
    }
  };

  const updatePreferences = async (preferences: IUserPreferences) => {
    try {
      const updatedUser = await httpService.updateUserPreferences(preferences);
      setUser((prevUser) => ({ ...prevUser, ...updatedUser }));
      return updatedUser;
    } catch (error) {
      console.error("Failed to update preferences:", error);
      throw error;
    }
  };

  const value = {
    user,
    permittedRoutes,
    isAuthenticated: !!httpService.getAccessToken(),
    isLoading,
    selectedAccount,
    selectedIntegration,
    teams,
    loggingOut,
    sessionId,
    wsConnected,
    lastPing,
    sendLogout,
    taskId,
    setTaskId,
    login,
    takeOverLogin,
    logout: enhancedLogout,
    signup,
    setUser,
    setSelectedAccount,
    setSelectedIntegration,
    updatePreferences,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
